import React, { useEffect, useState } from 'react';
import { UserCircle, Save, KeyRound, LogOut, Mail, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';

/* Mi cuenta, para el que usa el CRM.

   Hasta ahora una persona no tenía dónde ver ni corregir su propio nombre, su
   mail o su contraseña: había que pedírselo a la agencia. El rol y el cliente no
   se editan acá a propósito; eso lo define quien administra. */

const ROLES = {
  super_admin: 'Super administrador',
  admin: 'Administrador',
  user: 'Usuario',
  viewer: 'Solo lectura',
};

export default function MiCuenta() {
  const { user, logout, theme, setTheme } = useAuth();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ display_name: '', email: '' });
  const [guardando, setGuardando] = useState(false);
  const [pass, setPass] = useState({ current_password: '', new_password: '', repetir: '' });
  const [cambiando, setCambiando] = useState(false);

  useEffect(() => {
    api.get('/auth/mi-cuenta')
      .then(r => { setD(r.data); setForm({ display_name: r.data.display_name || '', email: r.data.email || '' }); })
      .catch(() => toast.error('No se pudo cargar tu cuenta'));
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put('/auth/mi-cuenta', form);
      toast.success('Listo, guardamos tus datos');
      setD({ ...d, ...form });
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };

  const cambiarPass = async (ev) => {
    ev.preventDefault();
    if (pass.new_password !== pass.repetir) return toast.error('Las contraseñas nuevas no coinciden');
    setCambiando(true);
    try {
      await api.put('/auth/change-password', {
        current_password: pass.current_password, new_password: pass.new_password,
      });
      toast.success('Contraseña actualizada');
      setPass({ current_password: '', new_password: '', repetir: '' });
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo cambiar'); }
    finally { setCambiando(false); }
  };

  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
        <UserCircle size={30} />
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>Mi cuenta</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Tus datos y tu acceso</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="form-group">
          <label>Nombre</label>
          <input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}
            placeholder="Cómo querés que te llamemos" />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} /> Mail</label>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="tu@mail.com" />
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5 }}>
            Es a donde te llegan los avisos y el código para entrar sin contraseña.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
            <Save size={16} /> {guardando ? 'Guardando...' : 'Guardar'}
          </button>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield size={13} /> Entrás como <b>{ROLES[d.role] || d.role}</b> · usuario <b>{d.username}</b>
          </span>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyRound size={17} /> Cambiar la contraseña
        </h3>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
          Si entrás siempre con el código al mail, no hace falta que tengas una.
        </div>
        <form onSubmit={cambiarPass}>
          <div className="form-group">
            <label>Contraseña actual</label>
            <input type="password" value={pass.current_password} autoComplete="current-password"
              onChange={e => setPass({ ...pass, current_password: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Contraseña nueva</label>
            <input type="password" value={pass.new_password} autoComplete="new-password"
              onChange={e => setPass({ ...pass, new_password: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Repetila</label>
            <input type="password" value={pass.repetir} autoComplete="new-password"
              onChange={e => setPass({ ...pass, repetir: e.target.value })} />
          </div>
          <button className="btn btn-secondary" type="submit"
            disabled={cambiando || !pass.current_password || !pass.new_password}>
            {cambiando ? 'Cambiando...' : 'Cambiar contraseña'}
          </button>
        </form>
      </div>

      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Apariencia</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Modo claro u oscuro, como prefieras.</div>
        </div>
        <ThemeToggle theme={theme} onChange={setTheme} />
        <button className="btn btn-ghost" onClick={logout}><LogOut size={16} /> Cerrar sesión</button>
      </div>
    </div>
  );
}
